// Settings panel for Firefly — assist level (peek aggressiveness),
// visual/motion intensity, and sidebar display mode (FF-DOCK-3).

import {
  ACTIVITY_DOCK_ASSIST_LEVEL_DESCRIPTIONS,
  type ActivityDockAssistLevel,
} from "@/lib/activityDock/assistLevel";
import {
  FIREFLY_VISUAL_INTENSITY_DESCRIPTIONS,
  type FireflyVisualIntensity,
} from "@/lib/firefly/fireflyVisualIntensity";
import { useFireflyDisplayMode } from "@/hooks/useFireflyDisplayMode";
import {
  dockFireflyInSidebar,
  returnFireflyToFloating,
} from "@/lib/firefly/sidebarFireflyDockActions";

export type SettingsDrawerActivityDockSectionProps = {
  level: ActivityDockAssistLevel;
  setLevel: (next: ActivityDockAssistLevel) => void;
  visualIntensity: FireflyVisualIntensity;
  setVisualIntensity: (next: FireflyVisualIntensity) => void;
  visualIntensityCapped?: boolean;
};

function IntensityRadioGroup<T extends string>({
  name,
  ariaLabel,
  options,
  value,
  onChange,
}: {
  name: string;
  ariaLabel: string;
  options: readonly { id: T; label: string; blurb: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <fieldset>
      <legend className="sr-only">{ariaLabel}</legend>
      <div className="space-y-2" role="radiogroup" aria-label={ariaLabel}>
        {options.map((opt) => {
          const checked = value === opt.id;
          return (
            <label
              key={opt.id}
              className={
                "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors " +
                (checked
                  ? "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/50"
                  : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-zinc-800/60")
              }
            >
              <input
                type="radio"
                name={name}
                value={opt.id}
                checked={checked}
                onChange={() => onChange(opt.id)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600 dark:accent-indigo-400"
              />
              <span className="block">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  {opt.label}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {opt.blurb}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function SettingsDrawerActivityDockSection({
  level,
  setLevel,
  visualIntensity,
  setVisualIntensity,
  visualIntensityCapped = false,
}: SettingsDrawerActivityDockSectionProps) {
  const { mode: fireflyDisplayMode } = useFireflyDisplayMode();
  const dockedInBell = fireflyDisplayMode === "docked";

  return (
    <section className="space-y-6">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
        Firefly
      </h3>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Assist level
        </h4>
        <IntensityRadioGroup
          name="activity-dock-assist-level"
          ariaLabel="Assist level"
          options={ACTIVITY_DOCK_ASSIST_LEVEL_DESCRIPTIONS}
          value={level}
          onChange={setLevel}
        />
        <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          Controls when Firefly opens the activity panel on its own. It is
          always one click away manually. Hold Firefly to ask (mobile).
          Ctrl+Shift+F or double-click Firefly (desktop).
        </p>
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-5 dark:border-gray-800">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Visual intensity
        </h4>
        <IntensityRadioGroup
          name="firefly-visual-intensity"
          ariaLabel="Visual intensity"
          options={FIREFLY_VISUAL_INTENSITY_DESCRIPTIONS}
          value={visualIntensity}
          onChange={setVisualIntensity}
        />
        {visualIntensityCapped ? (
          <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
            Your system requests reduced motion — intensity is capped at Calm
            until that setting is off.
          </p>
        ) : (
          <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            Controls how fast Firefly pulses, hovers, and highlights. This is
            separate from assist level.
          </p>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-5 dark:border-gray-800">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Display
        </h4>
        <label
          className={
            "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors " +
            (dockedInBell
              ? "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/50"
              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-zinc-800/60")
          }
        >
          <input
            type="checkbox"
            name="firefly-dock-in-sidebar"
            checked={dockedInBell}
            onChange={(e) => {
              if (e.target.checked) dockFireflyInSidebar({ animate: true });
              else returnFireflyToFloating();
            }}
            className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600 dark:accent-indigo-400"
          />
          <span className="block">
            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
              Dock Firefly in the notification bell
            </span>
            <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
              Hide the floating Firefly. Alerts, Ask, and tours open from the bell.
              Turn this off to float Firefly and hide the bell.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}
